<template>
  <div
    ref="elementRef"
    v-style="brandStyles[isFullHeight ? 'containerFullHeight' : 'container']?.(headerHeight, promoBarHeight)"
    class="relative"
    :class="isFullHeight ? 'children:h-inherit' : 'children:h-inherit'"
  >
    <define-heading-template>
      <div>
        <cms-base-eyebrow
          class="mb-2"
          v-bind="{
            eyebrowLogoResponsive,
            eyebrowText,
            eyebrowTextColor,
            position: { sm: textAlignmentSmall, md: textAlignmentLarge },
          }"
        />
        <h1 v-if="title" v-style:c="titleColor" :class="titleStyle">
          <base-link
            v-if="mediaLink"
            :data-cta="mediaLink.delocalizedTracking"
            :target="mediaLink.targetAttribute"
            :to="mediaLink.url"
            @click="handlePromotionClick('title')"
          >
            <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
            <span v-else>{{ title }}</span>
          </base-link>
          <template v-else>
            <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
            <span v-else>{{ title }}</span>
          </template>
        </h1>
      </div>
    </define-heading-template>
    <div
      class="relative grid overflow-hidden"
      :class="{ 'children:h-inherit': isFullHeight }"
    >
      <div class="relative flex grid-area-stack">
        <base-picture
          v-if="responsiveMedia.images"
          v-bind="responsiveMedia.images"
          class="full"
          fit="cover"
          :lazy
          :props-img="{ class: 'h-full' }"
        />
        <vf-video
          v-if="responsiveMedia.video"
          v-bind="responsiveMedia.video"
          ref="videoRef"
          :autoplay="!lazy"
          class="full"
          :controls
          fit="cover"
          loop
          :meta="responsiveMedia.video.meta?.sm"
          muted
        />
        <base-link
          v-if="mediaLink"
          aria-hidden="true"
          class="absolute-0"
          :data-cta="mediaLink.delocalizedTracking"
          tabindex="-1"
          :target="mediaLink.targetAttribute"
          :to="mediaLink.url"
          @click="handlePromotionClick('img')"
        />
        <cms-base-gradient
          v-if="gradientStyle && gradientStyle !== 'off'"
          v-bind="{ gradientTone, gradientStyle, gradientBreakpoints }"
          position="bottom"
        />
      </div>
      <div
        v-style:pb="{ sm: firstElement && isFullHeight && floatingNavigation ? '6.75rem' : '2.5rem', lg: 0 }"
        class="md:pointer-events-none"
        :class="[
          brandClasses.contentContainer,
          contentAlignmentSmall[overlaySmall],
          contentSpacingSmall[overlaySmall],
          contentAlignmentLarge[overlayLarge],
          { 'md:grid-area-stack': variant === 'standardHero' },
          { 'pt-30 lg:pt-24': firstElement && header.transparent },
        ]"
      >
        <div
          v-style:bg="backdrop"
          class="relative"
          :class="[
            brandClasses.content,
            { '<md:pt-8': hasCaption },
            contentSize[overlayWidth!] || 'w-full',
            overlaySmall?.includes('overlay') && backdrop ? 'p-2' : '<md:bg-transparent',
          ]"
        >
          <heading-template />
          <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle">
            <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
            <span v-else>{{ subtitle }}</span>
          </p>
          <cms-rich-text
            v-if="richText"
            v-style:c="richTextColor || subtitleStyle"
            class="pointer-within"
            :content="{ richText }"
          />
          <div
            v-if="linkType !== 'No-CTA' && targets.length"
            class="pointer-within i-flex"
            :class="[
              brandClasses.contentCtaContainer,
              { '<md:hidden': !overlaySmall?.includes('overlay') },
            ]"
          >
            <div
              :class="[
                brandClasses.contentCtaContainerInner,
                ctaAlignmentLarge[overlayLarge], equalTargetSizeClass,
              ]"
              :style="`--cols: repeat(${targets.length},minmax(0,1fr))`"
            >
              <cms-shared-button
                v-for="(target, i) in targets"
                :key="i"
                                :class="{ '': overlaySmall === 'center-overlay' }"
                v-bind="{ ...target, ...getTextLinkStyles(content) }"
                :size
                @click="handlePromotionClick(`cta${i + 1}`)"
              />
            </div>
          </div>
          <cms-rich-text
            v-if="richTextBelowTargets"
            v-style:c="richTextBelowTargetsColor || subtitleStyle"
            class="pointer-within"
            :class="{ '<md:hidden': !overlaySmall?.includes('overlay') }"
            :content="{ richText: richTextBelowTargets }"
          />
        </div>
        <cms-base-caption
          v-if="hasCaption"
          :class="{ 'pt-12': firstElement && header.transparent }"
          v-bind="{ captionColor, captionStyle, captionPosition, captionText }"
        />
      </div>
    </div>
    <div
      v-if="linkType !== 'No-CTA' && targets.length && !overlaySmall?.includes('overlay')"
      class="f-col gap-4 px-4 md:!hidden"
      :class="[
        equalTargetSize?.sm ? '' : ctaAlignmentSmall[overlaySmall],
        variant === 'standardHero' ? 'mt-6' : 'mt-2',
        equalTargetSizeClass,
      ]"
      :style="`--cols: repeat(${targets.length},minmax(0,1fr))`"
    >
      <cms-shared-button
        v-for="(target, i) in targets"
        :key="i"
        v-bind="{ ...target, ...getTextLinkStyles(content) }"
        :size
        @click="handlePromotionClick(`cta${i + 1}`)"
      />
    </div>
    <cms-rich-text
      v-if="richTextBelowTargets"
      v-style:c="richTextBelowTargetsColor || subtitleStyle"
      class="pointer-within mt-2 px-4 md:hidden"
      :class="[
        contentAlignmentSmall[overlaySmall],
        { '<md:hidden': overlaySmall?.includes('overlay') },
      ]"
      :content="{ richText: richTextBelowTargets }"
    />
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { HeroContent, OverlayLarge, OverlaySmall } from '#types/components/cms/hero'
import { SectionContextKey } from '#content/contexts/section'

const { controls = true, ...props } = defineProps<{
  content: HeroContent
  controls?: boolean
  paused?: boolean
  firstElement?: boolean
}>()

const {
  backdrop,
  captionPosition,
  captionStyle,
  captionText,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowText,
  eyebrowTextColor,
  gradientBreakpoints = { sm: true },
  gradientStyle,
  gradientTone,
  isFullHeight,
  linkType,
  media,
  mediaTarget,
  name,
  overlayLarge,
  overlaySmall,
  overlayWidth,
  promotionTracking,
  richText,
  richTextColor,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  subtitle,
  subtitleHtml,
  subtitleColor,
  subtitleStyle,
  captionColor = subtitleColor,
  targets = [],
  title,
  titleHtml,
  titleColor,
  titleStyle,
  variant = 'standardHero',
  mediaList
} = props.content

const [DefineHeadingTemplate, HeadingTemplate] = createReusableTemplate()

const { size } = useAppConfig().components.cms.sharedButton
const { brandClasses, brandStyles } = useAppConfig().components.cms.hero
const { floatingNavigation, promoBarHeight } = useAppConfig().components.vf.header
const { getMedia } = useCms()
const header = useHeaderStore()

const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const videoRef = ref<HTMLVideoElement>()
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = (mediaList || [media]).map(getMedia)[0]

const contentAlignmentLarge: Record<OverlayLarge, string> = {
  'left-left-top': 'md:items-start',
  'left-left-middle': 'md:items-center',
  'left-left-bottom': 'md:items-end',
  'left-right-middle': 'md:justify-end md:items-center',
  'center-left-middle': 'md:text-center md:items-center',
  'center-center-top': 'md:text-center md:justify-center md:items-start',
  'center-center-middle': 'md:text-center md:center',
  'center-center-bottom': 'md:text-center md:justify-center md:items-end',
  'center-right-middle': 'md:text-center md:justify-end md:items-center'
}

const contentAlignmentSmall: Record<OverlaySmall, string | any[]> = {
  'left-overlay': [{ '<md:grid-area-stack': variant === 'standardHero' }, '<md:pointer-events-none <md:items-center'],
  'left-bottom-overlay': [{ '<md:grid-area-stack': variant === 'standardHero' }, '<md:pointer-events-none <md:items-end'],
  'left-below': '<md:mt-6 <md:items-start',
  'center-overlay': [{ '<md:grid-area-stack': variant === 'standardHero' }, '<md:pointer-events-none <md:center <md:text-center'],
  'center-below': '<md:center <md:text-center'
}

const contentSpacingSmall: Record<OverlaySmall, string> = {
  'left-overlay': '',
  'left-bottom-overlay': '',
  'left-below': '<md:mt-6 <md:px-4',
  'center-overlay': '',
  'center-below': '<md:mt-6 <md:px-4'
}

const contentSize = {
  xs: 'w-10/12 md:w-1/3',
  sm: 'w-10/12 md:w-1/2',
  md: 'w-11/12 md:w-2/3',
  lg: 'w-full md:w-11/12'
}

const ctaAlignmentLarge: Partial<Record<OverlayLarge, string>> = {
  'center-left-middle': 'md:justify-center',
  'center-center-top': 'md:justify-center',
  'center-center-middle': 'md:justify-center',
  'center-center-bottom': 'md:justify-center',
  'center-right-middle': 'md:justify-center'
}

const ctaAlignmentSmall: Record<OverlaySmall, string> = {
  'left-overlay': '<md:items-start',
  'left-bottom-overlay': '<md:items-start',
  'left-below': '<md:items-start',
  'center-overlay': '<md:items-center',
  'center-below': '<md:items-center'
}

const headerHeight = computed(() => (header.transparent ? header.height.promoBar : header.height.full) || 0)

const textAlignmentLarge = overlayLarge?.split('-')[0] || 'left'
const textAlignmentSmall = overlaySmall?.split('-')[0] || 'center'

const hasCaption = captionText && captionPosition !== 'off'

const equalTargetSizeClass = [
  getValueForBreakpoint('sm', equalTargetSize) && '<md:grid <md:cols-1 <md:auto-rows-fr',
  getValueForBreakpoint('md', equalTargetSize) && '~md:grid ~md:cols-$cols ~md:auto-rows-fr ~md:items-stretch',
  getValueForBreakpoint('lg', equalTargetSize) && 'lg:grid lg:cols-$cols lg:auto-rows-fr lg:items-stretch'
].filter(Boolean).join(' ')

onMounted(() => videoRef.value?.play())

watch(() => props.paused, (paused) => paused ? videoRef.value?.pause() : videoRef.value?.play())
</script>
